/**
 * Лента заметок плана (DEV-1658).
 *
 * Стор, а не локальное состояние компонента: ленту показывают и колонкой справа
 * от стека (макет 9-5), и на весь экран в мит-режиме (макет 9-7). Это один и тот
 * же список, и переключение режима не должно перечитывать его с сервера — иначе
 * заметка, написанная секунду назад, на мгновение пропадала бы с экрана.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { createNote, deleteNote, listNotes, updateNote, type Note } from './notes'

export const useNotesStore = defineStore('plan-notes', () => {
  const items = ref<Note[]>([])
  const isLoading = ref(false)
  const isLoaded = ref(false)
  const error = ref<unknown>(null)

  /**
   * Заметка, которую сейчас разбирает ассистент.
   *
   * Признак живёт здесь, а не в компоненте: разбор идёт в панели ассистента, а
   * она переживает и уход с экрана, и смену режима. Без него применённый
   * черновик не к чему было бы привязать — панель знает только про созданные
   * задачи, но не про то, из какой мысли они выросли.
   */
  const parsingId = ref('')

  /**
   * Встреча, чью ленту показываем. Пусто — обычная лента: свои личные заметки
   * плюс заметки всех встреч, где человек был (DEV-1947).
   */
  const sessionId = ref('')

  /**
   * Сколько заметок всего против того, сколько влезло на экран.
   *
   * Длинный протокол обрезается по лимиту ленты, и молчать об этом нельзя:
   * человек читал бы кусок встречи, считая его целым.
   */
  const total = ref(0)

  const isTruncated = computed(() => total.value > items.value.length)

  const fresh = computed(() => items.value.filter((note) => !note.processedAt))

  /**
   * Номер запроса ленты.
   *
   * Переключение встречи и приход события идут вперемешку, а ответы
   * возвращаются в своём порядке: без номера ответ по старой сессии перерисовал
   * бы ленту новой — чужими заметками на экране.
   */
  let request = 0

  const load = async () => {
    isLoading.value = true

    const own = (request += 1)
    const wanted = sessionId.value

    try {
      const loaded = await listNotes(wanted)

      if (own !== request) return

      items.value = loaded.items
      total.value = loaded.total
      isLoaded.value = true
      error.value = null
    } catch (failure) {
      if (own !== request) return

      error.value = failure
    } finally {
      if (own === request) isLoading.value = false
    }
  }

  /**
   * Записать заметку. Список обновляется ответом сервера, а не своей копией:
   * идентификатор и время создания ставит он, а без идентификатора карточку
   * нечем ни разобрать, ни удалить.
   */
  const add = async (body: string): Promise<boolean> => {
    const text = body.trim()

    if (!text) return false

    try {
      const note = await createNote(text, sessionId.value)

      if (note) items.value = [note, ...items.value]

      return !!note
    } catch (failure) {
      // Об отказе обязан узнать вызывающий: набранный текст держит он, и
      // очистить поле, не сохранив, значит потерять сказанное на созвоне.
      error.value = failure

      return false
    }
  }

  const replace = (note: Note) => {
    items.value = items.value.map((item) => (item.id === note.id ? note : item))
  }

  /** Заметка разобрана: ключи вышедших из неё задач остаются в ней навсегда */
  const markProcessed = async (id: string, issueKeys: string[]) => {
    try {
      const note = await updateNote(id, { processed: true, issueKeys })

      if (note) replace(note)
    } catch (failure) {
      error.value = failure
    } finally {
      if (parsingId.value === id) parsingId.value = ''
    }
  }

  const remove = async (id: string) => {
    const saved = items.value

    items.value = items.value.filter((item) => item.id !== id)

    try {
      await deleteNote(id)
    } catch (failure) {
      // На встрече заметку мог снести сосед секундой раньше: «её нет» — это
      // ровно то, чего мы добивались, и возвращать её на экран незачем.
      if (isGone(failure)) return

      items.value = saved
      error.value = failure
    }
  }

  /** Правка заметки: на встрече текст поправляют прямо в карточке */
  const edit = async (id: string, body: string): Promise<boolean> => {
    const text = body.trim()

    if (!text) return false

    try {
      const note = await updateNote(id, { body: text })

      if (note) replace(note)

      return !!note
    } catch (failure) {
      error.value = failure

      return false
    }
  }

  /** Показать ленту одной встречи или вернуться к обычной */
  const setSession = async (id: string) => {
    if (sessionId.value === id) return

    sessionId.value = id
    await load()
  }

  const startParsing = (id: string) => {
    parsingId.value = id
  }

  return {
    items,
    fresh,
    sessionId,
    total,
    isTruncated,
    isLoading,
    isLoaded,
    error,
    parsingId,
    load,
    add,
    markProcessed,
    remove,
    edit,
    setSession,
    startParsing,
  }
})

/** Ответ «такой заметки нет»: её уже удалили, и это не ошибка нашего экрана */
const isGone = (failure: unknown): boolean =>
  (failure as { response?: { status?: number } })?.response?.status === 404
